import React, { useState, useMemo, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Chip,
  Alert,
  IconButton
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ScatterChart,
  Scatter
} from 'recharts';
import BarChartIcon from '@mui/icons-material/BarChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { getDataPointColor, getFallbackColor, hasTeamGrouping } from '../utils/teamColors';
import ZoomableChart from './common/ZoomableChart';
import { getAutoscaledDomain } from '../utils/chartDomainUtils';
import { qbButtonSx, qbCardSx, qbColors, qbFonts } from './queryBuilderTheme';

const darkChartChipSx = {
  bgcolor: 'rgba(255,255,255,0.035)',
  color: qbColors.textMed,
  borderColor: qbColors.borderStrong,
};

const darkChartAlertSx = {
  bgcolor: 'rgba(91,141,239,0.14)',
  color: qbColors.textMed,
  border: '1px solid rgba(91,141,239,0.34)',
  borderRadius: '10px',
  '& .MuiAlert-icon': { color: qbColors.blue },
};

const ChartPanel = forwardRef(({ data, groupBy, isVisible, onToggle, initialRecommendation = null, isMobile = false }, ref) => {
  const [charts, setCharts] = useState([]);
  const [nextChartId, setNextChartId] = useState(1);
  const [selectedPoints, setSelectedPoints] = useState({}); // Track selected points per chart: { chartId: payload }
  const lastAppliedRecommendationKeyRef = useRef(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    addBarChart,
    addLineChart,
    addScatterChart,
    applyRecommendedChart,
  }));

  // Chart management functions
  const pickPreferredMetric = (candidates, metricKeys, fallback = null) => {
    for (const candidate of candidates) {
      const normalized = String(candidate || '').trim().toLowerCase();
      if (normalized && metricKeys.includes(normalized)) {
        return normalized;
      }
    }
    return fallback;
  };

  const addBarChart = (config = {}) => {
    const metricKeys = availableMetrics.map(metric => metric.key);
    if (metricKeys.length === 0) {
      return false;
    }

    // Stacked / multi-metric bar: y_axis arrives as an array of column names.
    // Filter to known metrics and only stack if at least 2 survived.
    if (config.stacked && Array.isArray(config.y_axis)) {
      const stackedMetrics = config.y_axis
        .map((m) => String(m || '').trim().toLowerCase())
        .filter((m) => metricKeys.includes(m));
      if (stackedMetrics.length >= 2) {
        const newChart = {
          id: nextChartId,
          type: 'bar',
          selectedMetric: stackedMetrics[0],
          stackedMetrics,
        };
        setCharts(prev => [...prev, newChart]);
        setNextChartId(prev => prev + 1);
        return true;
      }
    }

    const fallbackMetric = metricKeys.includes('runs') ? 'runs' : metricKeys[0];
    // y_axis can be a single string, an array, or absent. Normalize for the
    // single-metric fallback path (e.g. recommendations that asked for a
    // stacked bar but only one metric is available).
    const yAxisCandidate = Array.isArray(config.y_axis) ? config.y_axis[0] : config.y_axis;
    const selectedMetric = pickPreferredMetric(
      [config.selectedMetric, config.yMetric, yAxisCandidate, config.xMetric, config.x_axis],
      metricKeys,
      fallbackMetric,
    );
    if (!selectedMetric) {
      return false;
    }

    const newChart = {
      id: nextChartId,
      type: 'bar',
      selectedMetric,
    };
    setCharts(prev => [...prev, newChart]);
    setNextChartId(prev => prev + 1);
    return true;
  };

  const addLineChart = (config = {}) => {
    const metricKeys = availableMetrics.map(metric => metric.key);
    if (metricKeys.length === 0) {
      return false;
    }
    const fallbackMetric = metricKeys.includes('strike_rate') ? 'strike_rate' : metricKeys[0];
    const yAxisCandidate = Array.isArray(config.y_axis) ? config.y_axis[0] : config.y_axis;
    const selectedMetric = pickPreferredMetric(
      [config.selectedMetric, config.yMetric, yAxisCandidate],
      metricKeys,
      fallbackMetric,
    );
    if (!selectedMetric) {
      return false;
    }
    const newChart = {
      id: nextChartId,
      type: 'line',
      selectedMetric,
    };
    setCharts(prev => [...prev, newChart]);
    setNextChartId(prev => prev + 1);
    return true;
  };

  const addScatterChart = (config = {}) => {
    const metricKeys = availableMetrics.map(metric => metric.key);
    if (metricKeys.length < 2) {
      return false;
    }

    const xMetric = pickPreferredMetric(
      [config.xMetric, config.x_axis, 'runs', 'balls', metricKeys[0]],
      metricKeys,
      metricKeys[0],
    );
    const yMetric = pickPreferredMetric(
      [config.yMetric, config.y_axis, 'strike_rate', 'average', metricKeys.find(metric => metric !== xMetric)],
      metricKeys.filter(metric => metric !== xMetric),
      metricKeys.find(metric => metric !== xMetric),
    );
    if (!xMetric || !yMetric || xMetric === yMetric) {
      return false;
    }

    const newChart = {
      id: nextChartId,
      type: 'scatter',
      xMetric,
      yMetric,
    };
    setCharts(prev => [...prev, newChart]);
    setNextChartId(prev => prev + 1);
    return true;
  };

  const applyRecommendedChart = (recommendation = null) => {
    if (!recommendation || !recommendation.type) {
      return false;
    }
    if (recommendation.type === 'bar') {
      return addBarChart(recommendation);
    }
    if (recommendation.type === 'scatter') {
      return addScatterChart(recommendation);
    }
    if (recommendation.type === 'line') {
      return addLineChart(recommendation);
    }
    return false;
  };

  const removeChart = (chartId) => {
    setCharts(prev => prev.filter(chart => chart.id !== chartId));
  };

  const updateChart = (chartId, updates) => {
    setCharts(prev => prev.map(chart => 
      chart.id === chartId ? { ...chart, ...updates } : chart
    ));
  };

  // Format metric values for display
  const formatMetricValue = (value, metric) => {
    if (value === null || value === undefined) return 'N/A';
    
    if (metric.includes('percentage') || metric === 'percent_balls') {
      return `${Number(value).toFixed(1)}%`;
    }
    if (metric === 'strike_rate' || metric === 'average' || metric === 'balls_per_dismissal') {
      return Number(value).toFixed(1);
    }
    if (typeof value === 'number' && value > 1000) {
      return value.toLocaleString();
    }
    
    return value;
  };

  // Generate nice, evenly spaced tick values for axes
  const generateNiceTicks = (min, max, targetCount = 5) => {
    if (min === max) return [min];
    
    const range = max - min;
    const roughStep = range / (targetCount - 1);
    
    // Find a "nice" step size
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const normalizedStep = roughStep / magnitude;
    
    let niceStep;
    if (normalizedStep < 1.5) {
      niceStep = 1 * magnitude;
    } else if (normalizedStep < 3) {
      niceStep = 2 * magnitude;
    } else if (normalizedStep < 7) {
      niceStep = 5 * magnitude;
    } else {
      niceStep = 10 * magnitude;
    }
    
    // Generate ticks
    const ticks = [];
    const startTick = Math.ceil(min / niceStep) * niceStep;
    
    for (let tick = startTick; tick <= max + niceStep * 0.01; tick += niceStep) {
      ticks.push(Math.round(tick * 1000) / 1000); // Round to avoid floating point issues
    }
    
    return ticks.length > 0 ? ticks : [min, max];
  };

  // Format axis tick values with rounding and nice spacing
  const formatAxisTick = (value, metric) => {
    if (value === null || value === undefined) return '';
    
    const numValue = Number(value);
    
    if (metric.includes('percentage') || metric === 'percent_balls') {
      return `${numValue.toFixed(0)}%`;
    }
    if (metric === 'strike_rate' || metric === 'average' || metric === 'economy_rate') {
      return numValue.toFixed(1);
    }
    if (numValue >= 1000) {
      return `${(numValue / 1000).toFixed(1)}k`;
    }
    if (numValue % 1 === 0) {
      return numValue.toString();
    }
    
    return numValue.toFixed(1);
  };

  // Calculate dynamic domain boundaries with nice tick spacing
  const getDataDomain = (dataKey) => {
    const values = chartData
      .map(d => d[dataKey])
      .filter(val => !isNaN(val) && val !== undefined && val !== null);
    
    if (values.length === 0) return [0, 100];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    
    // For scatter plots, use dynamic ranges that fit the data better
    // Only start from 0 for metrics that meaningfully start from 0
    let paddedMin, paddedMax;
    
    if (dataKey.includes('percentage') || dataKey === 'percent_balls') {
      // Percentages should start from 0
      paddedMin = 0;
      paddedMax = Math.min(100, max + range * 0.1);
    } else if (dataKey === 'wickets' || dataKey === 'runs' || dataKey === 'balls') {
      // Count-based metrics should start from 0
      paddedMin = 0;
      paddedMax = max + range * 0.1;
    } else {
      // For averages, strike rates, economy rates - use dynamic range
      paddedMin = Math.max(0, min - range * 0.15); // 15% padding below
      paddedMax = max + range * 0.15; // 15% padding above
      
      // Ensure minimum range for readability
      if (range < 5) {
        const center = (min + max) / 2;
        paddedMin = Math.max(0, center - 3);
        paddedMax = center + 3;
      }
    }
    
    return [paddedMin, paddedMax];
  };

  const getScatterDomain = (dataKey) => {
    const values = chartData
      .map((row) => row[dataKey])
      .filter((value) => !isNaN(value) && value !== undefined && value !== null);

    const isNonNegativeMetric =
      dataKey.includes('percentage') ||
      [
        'percent_balls',
        'wickets',
        'runs',
        'balls',
        'fours',
        'sixes',
        'dots',
        'boundaries',
        'average',
        'strike_rate',
        'economy',
        'economy_rate',
        'balls_per_dismissal',
        'balls_per_wicket',
      ].includes(dataKey);

    const fallbackDomain = dataKey.includes('percentage') || dataKey === 'percent_balls'
      ? [0, 100]
      : [0, 10];

    return getAutoscaledDomain(values, {
      paddingRatio: 0.1,
      stdDevThreshold: 2,
      clampMin: isNonNegativeMetric ? 0 : undefined,
      fallbackDomain,
    });
  };

  // Get available metrics from the data
  const availableMetrics = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const sampleRow = data[0];
    const predefinedMetrics = [
      { key: 'runs', label: 'Runs', color: '#8884d8' },
      { key: 'balls', label: 'Balls', color: '#82ca9d' },
      { key: 'wickets', label: 'Wickets', color: '#ffc658' },
      { key: 'dots', label: 'Dot Balls', color: '#ff7c7c' },
      { key: 'boundaries', label: 'Boundaries', color: '#8dd1e1' },
      { key: 'fours', label: 'Fours', color: '#d084d0' },
      { key: 'sixes', label: 'Sixes', color: '#87d068' },
      { key: 'strike_rate', label: 'Strike Rate', color: '#ffb347' },
      { key: 'average', label: 'Average', color: '#ff9999' },
      { key: 'dot_percentage', label: 'Dot %', color: '#ffcc99' },
      { key: 'boundary_percentage', label: 'Boundary %', color: '#99ccff' },
      { key: 'balls_per_dismissal', label: 'Balls/Wicket', color: '#cc99ff' },
      { key: 'percent_balls', label: '% Balls', color: '#ff6b6b' } // Add the new column
    ];

    // Filter to only include metrics that exist in the data
    const availableFromPredefined = predefinedMetrics.filter(metric => metric.key in sampleRow);
    
    // Dynamically add any other numeric columns that aren't in our predefined list
    const dynamicMetrics = [];
    Object.keys(sampleRow).forEach(key => {
      // Check if it's a numeric value and not already in our predefined list
      const value = sampleRow[key];
      const isNumeric = typeof value === 'number' && !isNaN(value);
      const isNotGrouping = !groupBy || !groupBy.includes(key);
      const isNotPredefined = !predefinedMetrics.some(m => m.key === key);
      const isNotInternal = !['is_summary', 'summary_level', 'displayIndex'].includes(key);
      
      if (isNumeric && isNotGrouping && isNotPredefined && isNotInternal) {
        dynamicMetrics.push({
          key,
          label: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          color: getFallbackColor(availableFromPredefined.length + dynamicMetrics.length)
        });
      }
    });

    return [...availableFromPredefined, ...dynamicMetrics];
  }, [data, groupBy]);

  useEffect(() => {
    const recommendationKey = initialRecommendation?.key;
    if (!isVisible || !recommendationKey) {
      return;
    }
    if (lastAppliedRecommendationKeyRef.current === recommendationKey) {
      return;
    }

    const applied = applyRecommendedChart(initialRecommendation);
    if (applied) {
      lastAppliedRecommendationKeyRef.current = recommendationKey;
    }
  }, [isVisible, initialRecommendation, availableMetrics, applyRecommendedChart]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!data || data.length === 0 || !groupBy || groupBy.length === 0) return [];

    // Check if we have team-based grouping
    const useTeamColors = hasTeamGrouping(groupBy);

    return data.map((row, index) => {
      // Create a display name from grouping columns with better formatting
      const groupingName = groupBy
        .map(col => {
          const value = row[col];
          if (col === 'year') return value;
          // Don't truncate team names - they're important for identification
          if (col === 'batting_team' || col === 'bowling_team' || col === 'team') {
            return value;
          }
          // Only truncate very long strings (>20 chars)
          if (typeof value === 'string' && value.length > 20) {
            return value.substring(0, 18) + '...';
          }
          return value;
        })
        .join(' • ');

      // Create a shorter name for chart labels
      const shortName = groupBy
        .map(col => {
          const value = row[col];
          if (col === 'year') return value;
          if (typeof value === 'string' && value.length > 10) {
            return value.substring(0, 8) + '..';
          }
          return value;
        })
        .join(' • ');

      // Get color - use team color if available, otherwise fallback to indexed color
      const pointColor = useTeamColors 
        ? getDataPointColor(row, index)
        : getFallbackColor(index);

      return {
        ...row,
        name: groupingName,
        shortName: shortName,
        displayIndex: index,
        teamColor: pointColor
      };
    });
  }, [data, groupBy]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label, chartConfig }) => {
    if (!active || !payload || !payload[0]) return null;

    const dataPoint = payload[0].payload;
    
    return (
      <Card sx={{ ...qbCardSx, p: 2, maxWidth: 300 }}>
        <Typography variant="subtitle2" gutterBottom>
          {dataPoint.name}
        </Typography>
        
        {chartConfig?.type === 'bar' ? (
          <Typography variant="body2" sx={{ color: qbColors.accent }}>
            {availableMetrics.find(m => m.key === chartConfig.selectedMetric)?.label}: {formatMetricValue(dataPoint[chartConfig.selectedMetric], chartConfig.selectedMetric)}
          </Typography>
        ) : (
          <>
            <Typography variant="body2">
              <strong>{availableMetrics.find(m => m.key === chartConfig.xMetric)?.label}:</strong> {formatMetricValue(dataPoint[chartConfig.xMetric], chartConfig.xMetric)}
            </Typography>
            <Typography variant="body2">
              <strong>{availableMetrics.find(m => m.key === chartConfig.yMetric)?.label}:</strong> {formatMetricValue(dataPoint[chartConfig.yMetric], chartConfig.yMetric)}
            </Typography>
          </>
        )}
        
        {/* Show additional context */}
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {dataPoint.balls && (
            <Chip size="small" label={`${dataPoint.balls.toLocaleString()} balls`} sx={darkChartChipSx} />
          )}
          {dataPoint.runs && (
            <Chip size="small" label={`${dataPoint.runs.toLocaleString()} runs`} sx={darkChartChipSx} />
          )}
        </Box>
      </Card>
    );
  };

  // Don't render if not visible or no data
  if (!isVisible || !data || data.length === 0 || !groupBy || groupBy.length === 0) {
    return null;
  }

  // Render individual bar chart
  const renderBarChart = (chart) => {
    const isStacked = Array.isArray(chart.stackedMetrics) && chart.stackedMetrics.length >= 2;
    const stackedData = isStacked
      ? chart.stackedMetrics.map((key) => ({
          key,
          metric: availableMetrics.find((m) => m.key === key),
        }))
      : null;
    const selectedMetricData = availableMetrics.find(m => m.key === chart.selectedMetric);
    // Phones: the first 25 rows (in the table's current sort). One 30px row per group made a
    // 398-batter leaderboard a 12,000px chart.
    const MOBILE_BAR_LIMIT = 25;
    const barData = isMobile ? chartData.slice(0, MOBILE_BAR_LIMIT) : chartData;
    const titleLabel = isStacked
      ? stackedData.map((d) => d.metric?.label || d.key).join(' + ')
      : (selectedMetricData?.label || chart.selectedMetric);

    return (
      <Card key={chart.id} sx={{ ...qbCardSx, mb: 3 }}>
        <CardContent>
          {/* Chart Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: qbFonts.display, color: qbColors.textHi }}>
              <BarChartIcon />
              Bar Chart - {titleLabel}
            </Typography>
            <IconButton
              size="small"
              onClick={() => removeChart(chart.id)}
              sx={{ color: qbColors.red }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Chart Controls */}
          {!isStacked && (
            <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 3 }}>
              <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <InputLabel>Metric</InputLabel>
                <Select
                  value={chart.selectedMetric}
                  onChange={(e) => updateChart(chart.id, { selectedMetric: e.target.value })}
                  label="Metric"
                >
                  {availableMetrics.map((metric) => (
                    <MenuItem key={metric.key} value={metric.key}>
                      {metric.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          {/* Bar Chart */}
          {/* Phones: horizontal bars, names down the left. Vertical bars rotated every category label
              -45deg with interval 0 at 10px, which for 10+ players was unreadable. */}
          <Box sx={{ width: '100%', height: isMobile ? Math.max(260, barData.length * 30 + 60) : 400 }}>
            <ResponsiveContainer>
              <BarChart
                data={barData}
                layout={isMobile ? 'vertical' : 'horizontal'}
                margin={isMobile ? { top: 8, right: 16, left: 4, bottom: 4 } : { top: 20, right: 30, left: 20, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={!isMobile} vertical={isMobile} />
                {isMobile ? (
                  <>
                    <XAxis type="number" fontSize={11} tickFormatter={(value) => formatAxisTick(value, isStacked ? chart.stackedMetrics[0] : chart.selectedMetric)}
                  {...(!isStacked && {
                    domain: getDataDomain(chart.selectedMetric),
                    ticks: generateNiceTicks(...getDataDomain(chart.selectedMetric), 6),
                  })} />
                    <YAxis type="category" dataKey="name" width={96} interval={0} fontSize={11} tickLine={false} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" height={40} interval={0} fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(value) => formatAxisTick(value, isStacked ? chart.stackedMetrics[0] : chart.selectedMetric)}
                  {...(!isStacked && {
                    domain: getDataDomain(chart.selectedMetric),
                    ticks: generateNiceTicks(...getDataDomain(chart.selectedMetric), 6),
                  })} />
                  </>
                )}
                <Tooltip content={(props) => <CustomTooltip {...props} chartConfig={chart} />} />
                <Legend />
                {isStacked
                  ? stackedData.map((d, idx) => (
                      <Bar
                        key={d.key}
                        dataKey={d.key}
                        stackId="a"
                        fill={d.metric?.color || getFallbackColor(idx)}
                        name={d.metric?.label || d.key}
                        radius={idx === stackedData.length - 1 ? (isMobile ? [0, 2, 2, 0] : [2, 2, 0, 0]) : 0}
                      />
                    ))
                  : (
                    <Bar
                      dataKey={chart.selectedMetric}
                      fill={selectedMetricData?.color || '#8884d8'}
                      name={selectedMetricData?.label || chart.selectedMetric}
                      radius={isMobile ? [0, 2, 2, 0] : [2, 2, 0, 0]}
                    />
                  )}
              </BarChart>
            </ResponsiveContainer>
          </Box>
          {isMobile && chartData.length > MOBILE_BAR_LIMIT && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: qbColors.textLo }}>
              Showing the first {MOBILE_BAR_LIMIT} of {chartData.length} rows, in the table&apos;s current order. Sort the table to change which rows appear.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderLineChart = (chart) => {
    const selectedMetricData = availableMetrics.find(m => m.key === chart.selectedMetric);
    return (
      <Card key={chart.id} sx={{ ...qbCardSx, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: qbFonts.display, color: qbColors.textHi }}>
              <ShowChartIcon />
              Line Chart - {selectedMetricData?.label || chart.selectedMetric}
            </Typography>
            <IconButton size="small" onClick={() => removeChart(chart.id)} sx={{ color: qbColors.red }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 3 }}>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Metric</InputLabel>
              <Select
                value={chart.selectedMetric}
                onChange={(e) => updateChart(chart.id, { selectedMetric: e.target.value })}
                label="Metric"
              >
                {availableMetrics.map((metric) => (
                  <MenuItem key={metric.key} value={metric.key}>
                    {metric.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Box sx={{ width: '100%', height: isMobile ? 300 : 400 }}>
            <ResponsiveContainer>
              <LineChart
                data={chartData}
                margin={{ top: 20, right: isMobile ? 12 : 30, left: isMobile ? 0 : 20, bottom: isMobile ? 8 : 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                {/* Flat labels; Recharts drops colliding ones on phones instead of rotating all of them. */}
                <XAxis
                  dataKey="name"
                  height={isMobile ? 24 : 40}
                  interval={isMobile ? 'preserveStartEnd' : 0}
                  minTickGap={6}
                  fontSize={isMobile ? 11 : 12}
                />
                <YAxis
                  tickFormatter={(value) => formatAxisTick(value, chart.selectedMetric)}
                  fontSize={12}
                  domain={getDataDomain(chart.selectedMetric)}
                  ticks={generateNiceTicks(...getDataDomain(chart.selectedMetric), 6)}
                />
                <Tooltip content={(props) => <CustomTooltip {...props} chartConfig={chart} />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey={chart.selectedMetric}
                  stroke={selectedMetricData?.color || '#8884d8'}
                  strokeWidth={2}
                  dot={false}
                  name={selectedMetricData?.label || chart.selectedMetric}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render individual scatter chart
  const renderScatterChart = (chart) => {
    const xMetricData = availableMetrics.find(m => m.key === chart.xMetric);
    const yMetricData = availableMetrics.find(m => m.key === chart.yMetric);
    
    const xDomain = getScatterDomain(chart.xMetric);
    const yDomain = getScatterDomain(chart.yMetric);

    // Get selected point for this chart from component-level state
    const selectedPoint = selectedPoints[chart.id] || null;
    const setSelectedPoint = (point) => {
      setSelectedPoints(prev => ({
        ...prev,
        [chart.id]: point
      }));
    };
    
    return (
      <Card key={chart.id} sx={{ ...qbCardSx, mb: 3 }}>
        <CardContent sx={{ px: isMobile ? 1 : 2 }}>
          {/* Chart Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: qbFonts.display, color: qbColors.textHi }}>
              <ScatterPlotIcon />
              {isMobile ? `${xMetricData?.label} vs ${yMetricData?.label}` : `Scatter Plot - ${xMetricData?.label} vs ${yMetricData?.label}`}
            </Typography>
            <IconButton
              size="small"
              onClick={() => removeChart(chart.id)}
              sx={{ color: qbColors.red }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Chart Controls */}
          <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 2 }}>
            <FormControl size={isMobile ? "small" : "medium"} sx={{ minWidth: isMobile ? 120 : 150 }}>
              <InputLabel>X-Axis</InputLabel>
              <Select
                value={chart.xMetric}
                onChange={(e) => updateChart(chart.id, { xMetric: e.target.value })}
                label="X-Axis"
              >
                {availableMetrics.map((metric) => (
                  <MenuItem key={metric.key} value={metric.key}>
                    {metric.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size={isMobile ? "small" : "medium"} sx={{ minWidth: isMobile ? 120 : 150 }}>
              <InputLabel>Y-Axis</InputLabel>
              <Select
                value={chart.yMetric}
                onChange={(e) => updateChart(chart.id, { yMetric: e.target.value })}
                label="Y-Axis"
              >
                {availableMetrics.map((metric) => (
                  <MenuItem key={metric.key} value={metric.key}>
                    {metric.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Mobile Selected Point Info */}
          {isMobile && selectedPoint && (
            <Card variant="outlined" sx={{ mb: 2, p: 1.5, bgcolor: qbColors.surface2, borderColor: qbColors.border }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {selectedPoint.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{xMetricData?.label}:</strong> {formatMetricValue(selectedPoint[chart.xMetric], chart.xMetric)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{yMetricData?.label}:</strong> {formatMetricValue(selectedPoint[chart.yMetric], chart.yMetric)}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedPoint.balls && (
                      <Chip size="small" label={`${selectedPoint.balls.toLocaleString()} balls`} />
                    )}
                    {selectedPoint.runs && (
                      <Chip size="small" label={`${selectedPoint.runs.toLocaleString()} runs`} />
                    )}
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setSelectedPoint(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Card>
          )}

          {/* Scatter Chart */}
          <Box sx={{ width: '100%', height: isMobile ? 350 : 500 }}>
            <ZoomableChart isMobile={isMobile}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  data={chartData}
                  margin={{
                    top: 10,
                    right: isMobile ? 5 : 30,
                    left: isMobile ? -15 : 20,
                    bottom: isMobile ? 20 : 40
                  }}
                  onClick={() => isMobile && setSelectedPoint(null)} // Clear selection when tapping empty area
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    type="number"
                    dataKey={chart.xMetric}
                    name={xMetricData?.label}
                    domain={xDomain}
                    ticks={generateNiceTicks(xDomain[0], xDomain[1], 5)}
                    tickFormatter={(value) => formatAxisTick(value, chart.xMetric)}
                    fontSize={isMobile ? 10 : 12}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <YAxis
                    type="number"
                    dataKey={chart.yMetric}
                    name={yMetricData?.label}
                    domain={yDomain}
                    ticks={generateNiceTicks(yDomain[0], yDomain[1], 5)}
                    tickFormatter={(value) => formatAxisTick(value, chart.yMetric)}
                    fontSize={isMobile ? 10 : 12}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    width={isMobile ? 35 : 60}
                  />
                  {!isMobile && (
                    <Tooltip content={(props) => <CustomTooltip {...props} chartConfig={chart} />} />
                  )}
                  <Scatter
                    data={chartData}
                    shape={(props) => {
                      const { cx, cy, payload } = props;
                      const isSelected = selectedPoint && selectedPoint.displayIndex === payload.displayIndex;
                      return (
                        <g
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isMobile) {
                              setSelectedPoint(isSelected ? null : payload);
                            }
                          }}
                        >
                          {/* Invisible larger touch target for mobile */}
                          <circle
                            cx={cx}
                            cy={cy}
                            r={24}
                            fill="transparent"
                            stroke="none"
                          />
                          {/* Visible point */}
                          <circle
                            cx={cx}
                            cy={cy}
                            r={7}
                            fill={payload.teamColor || '#8884d8'}
                            stroke={isSelected ? '#000' : '#fff'}
                            strokeWidth={isSelected ? 3 : 2}
                          />
                          {!isMobile && (
                            <text
                              x={cx}
                              y={cy + 18}
                              textAnchor="middle"
                              fontSize={11}
                              fontWeight="500"
                              fill="#c3c8d0"
                            >
                              {payload.shortName || payload.name}
                            </text>
                          )}
                        </g>
                      );
                    }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </ZoomableChart>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Main Header */}
      <Card sx={{ ...qbCardSx, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: qbFonts.display, color: qbColors.textHi }}>
              <BarChartIcon />
              Data Visualization
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon />}
              onClick={onToggle}
            >
              Hide Charts
            </Button>
          </Box>

          {/* Add Chart Buttons */}
          <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addBarChart}
              disabled={availableMetrics.length === 0}
              sx={{ ...qbButtonSx, minHeight: 36, fontSize: 11 }}
            >
              Add Bar Chart
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addScatterChart}
              disabled={availableMetrics.length < 2}
              sx={{ ...qbButtonSx, minHeight: 36, fontSize: 11 }}
            >
              Add Scatter Plot
            </Button>
          </Stack>

          {/* Info Chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip 
              label={`${chartData.length} data points`} 
              variant="outlined" 
              size="small" 
              sx={darkChartChipSx}
            />
            <Chip 
              label={`Grouped by: ${groupBy.join(', ')}`} 
              variant="outlined" 
              size="small" 
              sx={darkChartChipSx}
            />
            {charts.length > 0 && (
              <Chip 
                label={`${charts.length} active charts`} 
                size="small" 
                sx={{ bgcolor: qbColors.accent, color: qbColors.bg, fontWeight: 700 }}
              />
            )}
          </Box>

          {availableMetrics.length === 0 && (
            <Alert severity="info" sx={{ mt: 2, ...darkChartAlertSx }}>
              No numeric metrics available for visualization. Try grouping your data to see charts.
            </Alert>
          )}

          {availableMetrics.length === 1 && (
            <Alert severity="warning" sx={{ mt: 2, bgcolor: 'rgba(240,180,41,0.14)', color: qbColors.textMed, border: '1px solid rgba(240,180,41,0.34)', borderRadius: '10px', '& .MuiAlert-icon': { color: qbColors.gold } }}>
              Only one metric available. Add more grouping columns or metrics to enable scatter plots.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Render Charts */}
      {charts.map(chart => {
        if (chart.type === 'bar') return renderBarChart(chart);
        if (chart.type === 'line') return renderLineChart(chart);
        return renderScatterChart(chart);
      })}

      {/* Chart Tips */}
      {charts.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: qbColors.surface2, border: `1px solid ${qbColors.border}`, borderRadius: '16px' }}>
          <Typography variant="body2" sx={{ color: qbColors.textLo }}>
            <strong>Chart Tips:</strong> 
            {isMobile ? (
              " Tap data points for details. Use controls to change metrics and axes."
            ) : (
              " Hover over data points for detailed information. Scatter plots show team colors when available."
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

ChartPanel.displayName = 'ChartPanel';

export default ChartPanel;
